import React, { useEffect, useMemo, useRef, useState } from "react";
import Text from "../../Tags/Text";
import { Button } from "../../ui/button";
import Image from "../../Tags/Image";
import { useStoreBilling } from "../../../stores/useStoreBIlling";
import { checkoutPlan } from "../../../apis/workspace/billing";
import { useMutation } from "react-query";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const formatWithCommas = (value) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const isCreditsInTier = (credits, tier) => {
  if (!tier) return false;
  const min = tier?.min_credits ?? 0;
  const max = tier?.max_credits ?? Number.POSITIVE_INFINITY;
  return credits >= min && credits <= max;
};
const findTierForCredits = (tiers, credits) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return undefined;
  const matchingTier = tiers.find((tier) => isCreditsInTier(credits, tier));
  return matchingTier ?? tiers[tiers.length - 1];
};
const formatBreakpointLabel = (value) => {
  if (!Number.isFinite(value)) return "";

  if (value >= 1000) {
    const thousands = value / 1000;
    if (Number.isInteger(thousands)) {
      return `${thousands}k`;
    }
    return `${thousands.toFixed(1)}k`;
  }

  return formatWithCommas(value);
};

export default function OneTime({ activeWorkspace }) {
  const { one_time_credits } = useStoreBilling();
  const tiers = useMemo(
    () => (Array.isArray(one_time_credits?.tiers) ? one_time_credits.tiers : []),
    [one_time_credits]
  );

  const minOneTimeCredits = useMemo(() => {
    if (typeof one_time_credits?.min_quantity === "number") {
      return one_time_credits.min_quantity;
    }
    if (tiers.length > 0) {
      return tiers[0]?.min_credits ?? 0;
    }
    return 0;
  }, [one_time_credits?.min_quantity, tiers]);

  const maxOneTimeCredits = useMemo(() => {
    if (typeof one_time_credits?.max_quantity === "number") {
      return one_time_credits.max_quantity;
    }
    if (tiers.length > 0) {
      return tiers[tiers.length - 1]?.max_credits ?? minOneTimeCredits;
    }
    return minOneTimeCredits;
  }, [one_time_credits?.max_quantity, tiers, minOneTimeCredits]);

  const sliderStep = 100;

  const [selectedOneTimeCredits, setSelectedOneTimeCredits] = useState(minOneTimeCredits);
  const captureEvent = useCaptureEvent();
  const lastSliderLogRef = useRef(null);

  useEffect(() => {
    if (minOneTimeCredits >= maxOneTimeCredits) {
      setSelectedOneTimeCredits(minOneTimeCredits);
      return;
    }

    setSelectedOneTimeCredits((previous) => {
      if (typeof previous !== "number" || Number.isNaN(previous)) {
        return minOneTimeCredits;
      }
      return clamp(previous, minOneTimeCredits, maxOneTimeCredits);
    });
  }, [minOneTimeCredits, maxOneTimeCredits]);

  const selectedTier = useMemo(
    () => findTierForCredits(tiers, selectedOneTimeCredits),
    [tiers, selectedOneTimeCredits]
  );

  const selectedTierRangeText = useMemo(() => {
    if (!selectedTier) return null;
    const minRange = selectedTier?.min_credits ?? minOneTimeCredits;
    const maxRange = selectedTier?.max_credits ?? maxOneTimeCredits;

    if (typeof minRange !== "number" || Number.isNaN(minRange)) return null;
    if (typeof maxRange !== "number" || Number.isNaN(maxRange)) {
      return `${formatWithCommas(minRange)}+ credits`;
    }

    if (minRange === maxRange) {
      return `${formatWithCommas(minRange)} credits`;
    }

    return `${formatWithCommas(minRange)} - ${formatWithCommas(maxRange)} credits`;
  }, [selectedTier, minOneTimeCredits, maxOneTimeCredits]);

  const currencyCode = useMemo(() => {
    if (typeof one_time_credits?.currency === "string") {
      return one_time_credits.currency.toUpperCase();
    }
    return "USD";
  }, [one_time_credits?.currency]);

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }
  }, [currencyCode]);

  const unitAmountCents = Number(selectedTier?.unit_amount_cents ?? 0);
  const unitAmount = unitAmountCents / 100;
  const formattedPrice = currencyFormatter.format((selectedOneTimeCredits ?? 0) * unitAmount);

  const perCreditDisplay = useMemo(() => {
    if (!Number.isFinite(unitAmountCents)) {
      return currencyFormatter.format(unitAmount);
    }

    if (currencyCode === "USD") {
      const centsValue = unitAmountCents;
      const formattedCents = Number.isInteger(centsValue) ? centsValue.toString() : centsValue.toFixed(1);
      return `$ ${(formattedCents / 100).toFixed(2)}`;
    }

    return currencyFormatter.format(unitAmount);
  }, [currencyCode, currencyFormatter, unitAmount, unitAmountCents]);

  const sliderProgress =
    maxOneTimeCredits > minOneTimeCredits
      ? ((selectedOneTimeCredits - minOneTimeCredits) / (maxOneTimeCredits - minOneTimeCredits)) * 100
      : 0;
  const clampedSliderProgress = Math.min(Math.max(sliderProgress, 0), 100);
  const sliderBreakpoints = useMemo(() => {
    const breakpointCount = 10;
    if (maxOneTimeCredits <= minOneTimeCredits || breakpointCount < 2) {
      return [];
    }

    const range = maxOneTimeCredits - minOneTimeCredits;
    return Array.from({ length: breakpointCount }, (_, index) => {
      const ratio = index / (breakpointCount - 1);
      const value = Math.round(minOneTimeCredits + ratio * range);
      const labelValue = Math.round(value / 1000) * 1000;
      return {
        value,
        index,
        count: breakpointCount,
        percent: ratio * 100,
        label: formatBreakpointLabel(labelValue),
      };
    });
  }, [minOneTimeCredits, maxOneTimeCredits]);
  const selectedOneTimePhoneCount = Math.round(selectedOneTimeCredits / 5);
  const sliderDisabled = minOneTimeCredits >= maxOneTimeCredits;

  const checkoutPlanFn = useMutation("checkout_plan", checkoutPlan, {
    onSuccess(data, variables, context) {
      console.log("checkout_plan", data);
      if (data?.url) {
        window.location.href = data.url;
      }
      captureEvent("BP_OneTimePurchaseSuccess", {
        credits: variables?.data?.quantity,
      });
      //   if (data?.url) {
      //     window.open(data.url, "_blank");
      //   }
    },
    onError(error) {
      console.log(error);
      captureEvent("BP_OneTimePurchaseFailure", {
        credits: selectedOneTimeCredits,
        error: error?.message || "unknown_error",
      });
    },
  });
  const handleCheckoutPlan = () => {
    if (checkoutPlanFn.isLoading) {
      return;
    }
    captureEvent("BP_OneTimePurchaseClick", {
      credits: selectedOneTimeCredits,
    });
    checkoutPlanFn.mutate({
      data: {
        quantity: selectedOneTimeCredits,
        success_url: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/settings/billing/plans`,
        cancel_url: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/settings/billing/plans`,
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  return (
    <div>
      <div id="pricing_slider_range" className="mt-6">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-medium text-gray-500">Select credits</p>
          <p className="text-sm font-semibold text-gray-900">
            {formatWithCommas(selectedOneTimeCredits)} credits
          </p>
        </div>
        <div className="relative mt-4 h-6">
          <div className="absolute top-1/2 left-0 right-0 h-2 -translate-y-1/2 rounded-full bg-gray-200" />
          <div
            className="absolute top-1/2 left-0 h-2 -translate-y-1/2 rounded-full bg-[#7C3AED]"
            style={{ width: `${clampedSliderProgress}%` }}
          />
          <input
            type="range"
            min={minOneTimeCredits}
            max={maxOneTimeCredits}
            step={sliderStep}
            value={selectedOneTimeCredits}
            disabled={sliderDisabled}
            onChange={(event) => {
              const nextValue = Number(event.target.value);
              setSelectedOneTimeCredits(nextValue);
              const now = Date.now();
              if (!lastSliderLogRef.current || now - lastSliderLogRef.current > 1000) {
                captureEvent("BP_OneTimeCreditsChange", {
                  credits: nextValue,
                });
                lastSliderLogRef.current = now;
              }
            }}
            aria-label="Select one-time credits"
            className="relative z-10 w-full cursor-pointer appearance-none bg-transparent focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[#7C3AED] [&::-webkit-slider-thumb]:shadow [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[#7C3AED] [&::-moz-range-thumb]:shadow"
          />
        </div>
        {sliderBreakpoints.length > 0 && (
          <div className="relative mt-2 h-4 text-[10px] font-medium text-gray-400">
            {sliderBreakpoints.map((breakpoint) => {
              const isFirst = breakpoint.index === 0;
              const isLast = breakpoint.index === breakpoint.count - 1;
              let translateX = "-50%";

              if (isFirst) {
                translateX = "0";
              } else if (isLast) {
                translateX = "-100%";
              }

              return (
                <span
                  key={`${breakpoint.percent}-${breakpoint.value}`}
                  className="absolute left-0 top-0 whitespace-nowrap text-center"
                  style={{ left: `${breakpoint.percent}%`, transform: `translateX(${translateX})` }}
                >
                  {breakpoint.label}
                </span>
              );
            })}
          </div>
        )}
        {/* <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
          {tiers.map((tier) => {
            const key = tier?.name ?? tier?.display_name ?? `${tier?.min_credits}-${tier?.max_credits}`;
            const isActive = isCreditsInTier(selectedOneTimeCredits, tier);
            const label = tier?.display_name ?? tier?.name ?? `${formatWithCommas(tier?.min_credits ?? 0)}+`;

            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedOneTimeCredits(tier?.min_credits ?? minOneTimeCredits)}
                className={`cursor-pointer border-0 bg-transparent p-0 font-medium transition-colors hover:text-[#7C3AED] ${
                  isActive ? "text-[#7C3AED]" : "text-gray-500"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div> */}
      </div>
      <div className="flex space-x-3 items-start mt-4">
        <div className="w-full">
          <div className="leading-5 border-border border bg-background py-4 px-6 rounded-xl">
            <Text size="lg">One-time</Text>
            <Text>Credits will remain active up to an year</Text>
            <div className="flex my-7 justify-between items-center">
              <div className="inline-flex">
                <Text size="h4">{formattedPrice}</Text>
                <div className="ml-3">
                  <p>{currencyCode}</p>
                  <p className="text-xs text-gray-500">Billed once</p>
                </div>
              </div>
              <div className="w-6/12">
                {selectedTier && (
                  <p className="text-xs text-gray-500 mb-2">
                    {(selectedTier.display_name ?? selectedTier.name ?? "Selected tier").toString()} ·
                    {` ${perCreditDisplay} per credit`}
                    {selectedTierRangeText ? ` · ${selectedTierRangeText}` : ""}
                  </p>
                )}

                <Button
                  className=" w-full mb-6"
                  onClick={handleCheckoutPlan}
                  disabled={checkoutPlanFn.isLoading}
                >
                  <svg
                    width="17"
                    height="20"
                    viewBox="0 0 17 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.33354 1.66669L1.91142 10.5732C1.62075 10.922 1.47541 11.0964 1.47319 11.2437C1.47126 11.3718 1.52832 11.4936 1.62792 11.5741C1.7425 11.6667 1.96952 11.6667 2.42357 11.6667H8.50021L7.66688 18.3334L15.089 9.42681C15.3797 9.078 15.525 8.9036 15.5272 8.7563C15.5292 8.62826 15.4721 8.50643 15.3725 8.42594C15.2579 8.33335 15.0309 8.33335 14.5768 8.33335H8.50021L9.33354 1.66669Z"
                      stroke="currentColor"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <span className="ml-2">Purchase</span>
                  {checkoutPlanFn.isLoading && <div className="loader_circle ml-2 inline-block"></div>}
                </Button>
              </div>
            </div>

            <div className="space-y-3 pb-4 border-b">
              <div className="flex items-center">
                <Image src="/images/icons/billing/stack.svg" width="16" className="inline-block no-invert" />
                <span className="ml-2">
                  {`${formatWithCommas(selectedOneTimeCredits)} emails or ${formatWithCommas(selectedOneTimePhoneCount)} phone numbers`}
                </span>
              </div>
              <div className="flex items-center">
                <Image src="/images/icons/billing/user.svg" width="16" className="inline-block no-invert" />
                <span className="ml-2">Unlimited user seats</span>
              </div>
              <div className="flex items-center">
                <Image
                  src="/images/icons/billing/bag-uparrow.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">All credits granted upfront</span>
              </div>
            </div>
            <div className="space-y-3 pb-5 border-b mt-5">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center">
                  <Image
                    src="/images/icons/billing/email.svg"
                    width="16"
                    className="inline-block no-invert"
                  />
                  <span className="ml-2">Reveal contact’s emails</span>
                </div>
                <div className="inline-flex items-center">
                  <span className="mr-2">1</span>
                  <Image
                    src="/images/icons/billing/credit.svg"
                    width="16"
                    className="inline-block no-invert"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center">
                  <Image
                    src="/images/icons/billing/phone.svg"
                    width="16"
                    className="inline-block no-invert"
                  />
                  <span className="ml-2">Reveal contact’s phones</span>
                </div>
                <div className="inline-flex items-center">
                  <span className="mr-2">5</span>
                  <Image
                    src="/images/icons/billing/credit.svg"
                    width="16"
                    className="inline-block no-invert"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center">
                  <Image
                    src="/images/icons/billing/company.svg"
                    width="16"
                    className="inline-block no-invert"
                  />
                  <span className="ml-2">Reveal companies info</span>
                </div>
                <div className="inline-flex items-center">
                  <span className="mr-2">3</span>
                  <Image
                    src="/images/icons/billing/credit.svg"
                    width="16"
                    className="inline-block no-invert"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3 mt-5">
              <div className="flex items-center">
                <Image
                  src="/images/icons/untitledui/check-purple-outline.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">Work Email and Phone</span>
              </div>
              <div className="flex items-center">
                <Image
                  src="/images/icons/untitledui/check-purple-outline.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">8+ Enterprise data provider waterfall</span>
              </div>
              <div className="flex items-center">
                <Image
                  src="/images/icons/untitledui/check-purple-outline.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">Multi level email and phone verification</span>
              </div>
              <div className="flex items-center">
                <Image
                  src="/images/icons/untitledui/check-purple-outline.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">Bulk CSV Enrichment</span>
              </div>
              <div className="flex items-center">
                <Image
                  src="/images/icons/untitledui/check-purple-outline.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">150+ Company data points</span>
              </div>
              <div className="flex items-center">
                <Image
                  src="/images/icons/untitledui/check-purple-outline.svg"
                  width="16"
                  className="inline-block no-invert"
                />
                <span className="ml-2">LinkedIn and Sales Navigator Extension</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
